// noinspection JSUnresolvedReference
console.debug( 'Advanced Sidebar Accordions - Loaded' );

import {AccordionDataFormat, type IconType} from './gutenberg/blocks/Accordion';
import legacyAsmPro, {getLegacyObject} from './accordions/legacy-asm-pro';
import {KeysOfType} from '@lipemat/js-boilerplate/utility';

/**
 * Advanced Sidebar Menu PRO accordions.
 *
 * Widget, accordion functionality on front-end.
 *
 * @author OnPoint Plugins
 * @copyright OnPoint Plugins 2019-2025
 * @link https://onpointplugins.com/advanced-sidebar-menu/advanced-sidebar-menu-pro-widget-docs/advanced-sidebar-menu-pro-widget-accordions/
 */

export type SvgInHtml = HTMLElement & SVGElement;

export type AccordionsEnabled = {
	[ widgetId: string ]: AccordionDataFormat & {
		el?: JQuery<HTMLDivElement>;
	}
}

export type BooleanFields = KeysOfType<Required<AccordionDataFormat>, boolean | 'checked'>

const config = window.ADVANCED_SIDEBAR_MENU_ACCORDIONS;
window.advancedSidebarMenuPro = window.advancedSidebarMenuPro || {
	accordions: {
		enabled: {},
		expandSubMenu,
		init,
		removeAccordion,
	},
};
window.advancedSidebarMenuPro.accordions.init = init;
window.advancedSidebarMenuPro.accordions.removeAccordion = removeAccordion;

// Combine any legacy customizations with plugin accordions.
let accordions: AccordionsEnabled = getAccordions();

// Enable legacy global variables.
legacyAsmPro( {
	accordions,
	_expand_sub_menu: expandSubMenu,
	init,
	isChecked,
} );


/**
 * Iterate through the registered accordions
 * 1. Sets up the icons.
 * 2. Hides the child menus.
 *
 * @param {boolean} force - To initialize the accordion even if already
 *                        initialized. For edge cases and custom extensions.
 */
function init( force: boolean = false ): void {
	let parents: JQuery<HTMLElementTagNameMap['li']>;

	// If a new widget was added since the module loaded.
	accordions = getAccordions();

	for ( const widgetId in accordions ) {
		if ( ! accordions.hasOwnProperty( widgetId ) || ! isChecked( widgetId, 'enable_accordion' ) ) {
			continue;
		}

		let el: JQuery<HTMLDivElement> | undefined = $( '[data-js="' + widgetId + '"]' );
		// The selected element was passed. Most likely from Gutenberg FSE.
		if ( 'undefined' !== typeof accordions[ widgetId ].el ) {
			el = accordions[ widgetId ].el;
		}
		// Something went wrong. No element found.
		if ( 'undefined' === typeof el ) {
			continue;
		}

		// Already enabled. Prevent icons from doubling up.
		if ( 1 === el.data( 'accordion-enabled' ) ) {
			// Reinitialize.
			if ( force ) {
				el.find( '[data-accordion-icon]' ).remove();
			} else {
				continue;
			}
		}

		// The .has() only target elements that have actual children in.
		// Case of 'always display child pages' not being active.
		if ( isChecked( widgetId, 'include_parent' ) && ! isChecked( widgetId, 'include_parent_in_accordion' ) ) {
			parents = el.find<HTMLLIElement>( '> ul > li .has_children' ).has( 'ul' );
		} else {
			parents = el.find<HTMLLIElement>( 'ul .has_children' ).has( 'ul' );
		}

		parents.each( function() {
			const $menuItem = $( this );
			let $icon: JQuery<SvgInHtml>;
			// The current page and parents of current page should be open.
			if ( ! isChecked( widgetId, 'keep_closed' ) &&
				(
					$menuItem.hasClass( 'current-menu-ancestor' ) ||
					$menuItem.hasClass( 'current-menu-parent' ) ||
					$menuItem.hasClass( 'current-menu-item' ) ||
					$menuItem.find( '.current-menu-item' ).length !== 0
				)
			) {
				$menuItem
					.addClass( 'open' )
					.find( '> ul' )
					.first()
					.attr( 'aria-hidden', 'false' );
				$icon = getIcon( widgetId, 'contract', $menuItem );

				// Everything else should be closed.
			} else {
				$menuItem
					.addClass( 'closed' )
					.find( '> ul' )
					.first()
					.attr( 'aria-hidden', 'true' )
					.hide();
				$icon = getIcon( widgetId, 'expand', $menuItem );
			}

			if ( 0 === $menuItem.find( '[data-accordion-icon]' ).length ) {
				$menuItem.find( '> a' ).first().append( $icon ); //phpcs:ignore
				attachLinkHandlers( widgetId, $menuItem );
			}
		} );

		// Set a data flag to know when accordions are active.
		// Use an attribute so users can see it in the HTML.
		el.attr( 'data-accordion-enabled', 1 );
	}
}


/**
 * Icon was clicked. We are either going to expand
 * or contract a menu based on the icon's state.
 * After the menu is changed the icon will change
 * to the new one here.
 *
 * @param {JQuery<HTMLLIElement>} $menuItem - Menu item we are working with.
 * @param {JQuery<SvgInHtml>}     $icon     - Icon which was clicked.
 */
function expandSubMenu( $menuItem: JQuery<HTMLLIElement>, $icon: JQuery<SvgInHtml> ): void {
	let $newIcon: JQuery<SvgInHtml>;
	if ( 'expand' === $icon.data( 'state' ) ) {
		$menuItem
			.addClass( 'open' )
			.removeClass( 'closed' );
		$menuItem.find( '> ul' )
			.first()
			.attr( 'aria-hidden', 'false' )
			.slideDown();
		$newIcon = getIcon( $icon.data( 'widget_id' ), 'contract', $menuItem );
	} else {
		$menuItem
			.addClass( 'closed' )
			.removeClass( 'open' );
		$menuItem
			.find( '> ul' )
			.first()
			.attr( 'aria-hidden', 'true' )
			.slideUp();
		$newIcon = getIcon( $icon.data( 'widget_id' ), 'expand', $menuItem );
	}

	$icon.replaceWith( $newIcon ); //phpcs:ignore
	$newIcon.trigger( 'focus' );
}


/**
 * Get open/close icon for the menu item.
 *
 * @param {string}                widgetId  - Id of this widget.
 * @param {string}                state     - expand|contract.
 * @param {JQuery<HTMLLIElement>} $menuItem - The menu item we are working with.
 *
 * @return {JQuery<SvgInHtml>}
 */
function getIcon( widgetId: string, state: 'expand' | 'contract', $menuItem: JQuery<HTMLLIElement> ): JQuery<SvgInHtml> {
	let style: IconType | '' = accordions[ widgetId ]?.[ config.iconStyle ] ?? 'plus';
	if ( '' === style ) {
		style = 'plus';
	}
	const $icon: JQuery<SvgInHtml> = $( config.icons[ style ][ state ] );
	$icon.data( 'state', state );
	$icon.data( 'widget_id', widgetId );
	$icon.attr( 'data-accordion-icon', 1 );

	return attachClickHandler( $icon, $menuItem ) as JQuery<SvgInHtml>;
}


/**
 * Attach a click and keypress handler to an element to
 * make it open/close a section of the accordion.
 *
 * This provided icon holds the state and replaces itself
 * each time the state changes.
 *
 * @param {JQuery<SvgInHtml|HTMLLinkElement>} $el       - Element to attach the handler.
 * @param {JQuery<HTMLLIElement>}             $menuItem - Item of the menu to open/close.
 *
 * @return {JQuery<SvgInHtml|HTMLLinkElement>}
 */
function attachClickHandler( $el: JQuery<SvgInHtml | HTMLLinkElement>, $menuItem: JQuery<HTMLLIElement> ): JQuery<SvgInHtml | HTMLLinkElement> {
	const expand = function( ev: JQuery.ClickEvent | JQuery.KeyPressEvent ) {
		const possible = [ 'SPAN', 'PATH', 'SVG', 'G', 'RECT' ];
		// Don't trigger link handlers on click of icon span/svg (prevents double-up).
		if (
			possible.includes( ev.target.nodeName.toUpperCase() ) &&
			'A' === ev.currentTarget.nodeName &&
			(
				'undefined' !== typeof ev.target.dataset.accordionIcon ||
				$( ev.target ).parents( '[data-accordion-icon]' ).length > 0
			)
		) {
			return;
		}

		ev.preventDefault();
		const link = $menuItem.find<HTMLLinkElement>( '> a' ).first();
		expandSubMenu( $menuItem, link.find<SvgInHtml>( '[data-accordion-icon]' ) );
	};
	// Mouse clicked on the element.
	$el.off( 'click' );
	$el.on( 'click', function( ev ) {
		expand( ev );
	} );
	// Enter key pressed by screen readers.
	$el.off( 'keypress' );
	$el.on( 'keypress', function( ev ) {
		if ( 'Enter' === ( 'undefined' !== typeof ev.code ? ev.code : ev.key ).toString() ) {
			expand( ev );
		}
	} );
	return $el;
}


/**
 * Attach click handlers to links which match a level set to
 * do so. If a click handler is attached, the link will no longer
 * open it's URL on click, but instead open/close the accordion
 *
 * @param {string}                widgetId  - Id of widget.
 * @param {JQuery<HTMLLIElement>} $menuItem - <li> menu item.
 */
function attachLinkHandlers( widgetId: string, $menuItem: JQuery<HTMLLIElement> ): void {
	if ( ! isChecked( widgetId, 'links_expand' ) ) {
		return;
	}

	const menuLevel = parseInt( $menuItem.parent().data( 'level' ) );
	// Determine if this link is included based on menu level.
	const attach = accordions[ widgetId ].links_expand_levels.some( function( level ) {
		switch ( true ) {
			case 'highest' === level && 0 === menuLevel:
			case 'child' === level && 1 === menuLevel:
			case 'grandchild' === level && 2 <= menuLevel:
			case 'all' === level:
				return true;
		}
		return false;
	} );

	if ( attach ) {
		const link = $menuItem.find<HTMLLinkElement>( '> a' ).first();
		attachClickHandler( link, $menuItem );
	}
}


/**
 * Is a checkbox from a widget of a key checked.
 *
 * Done this way to allow for using asm_debug overrides to uncheck options.
 */
function isChecked( widgetId: string, option: BooleanFields ): boolean {
	return ( typeof accordions[ widgetId ][ option ] !== 'undefined' ) && 'checked' === accordions[ widgetId ][ option ];
}

/**
 * Get the accordions from the global variables.
 *
 * Merge the old variable into the new one for external customizations.
 */
function getAccordions(): AccordionsEnabled {
	return {
		...window.asm_pro?.enabled_accordions ?? {},
		...window.advancedSidebarMenuPro?.accordions.enabled ?? {},
	};
}

/**
 * Remove an accordion from the global variables.
 *
 * Used during previews to reset the accordion state on front-end
 * page builders.
 *
 * @since 9.7.1
 */
function removeAccordion( widgetId: string ): void {
	delete window.asm_pro?.enabled_accordions[ widgetId ];
	delete window.advancedSidebarMenuPro?.accordions.enabled[ widgetId ];

	console.debug( 'Advanced Sidebar Accordions - Removed', widgetId );
}


/**
 * Wait for the page to be ready then trigger the init.
 *
 * - Trigger events for 3rd party customizations
 * - Handle Elementor and Beaver Builder.
 */
( function() {
	$( function() {
		setTimeout( function() {
			// Enables accordions when editing Elementor.
			if ( 'undefined' !== typeof window.elementorFrontend ) {
				init( true );
				window.elementorFrontend.hooks.addAction( 'frontend/element_ready/widget', function() {
					init( true );
				} );
				// #9064 - Elementor popups require icon regeneration.
				$( document ).on( 'elementor/popup/show', function() {
					init( true );
				} );
			}

			// Enables accordions when editing Beaver Builder.
			$( '.fl-builder-content' ).on( 'fl-builder.layout-rendered', function() {
				init();
			} );
		}, 1000 );

		const oldAsmPro = getLegacyObject();
		// Legacy event trigger. Deprecated in favor of 'advanced-sidebar-menu-pro/accordions/available'
		// @todo Remove in version 10.
		$( document ).trigger( 'advanced-sidebar-menu-pro/accordions/loaded', [ oldAsmPro.accordion, oldAsmPro ] );
	} );

	init();

	const oldAsmPro = getLegacyObject();
	$( document ).trigger( 'advanced-sidebar-menu-pro/accordions/available', [ oldAsmPro.accordion, oldAsmPro ] );
}() );
